import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import ConduitHealthLoader from '~/components/Loader/ConduitHealthLoader';
import { cn } from '~/lib/utils';

/*
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)*/

const buttonVariants = cva(
  'text-black rounded-lg px-5 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-60',
  {
    variants: {
      variant: {
        primary:
          'border border-transparent bg-teal-600 text-white hover:bg-teal-500 disabled:hover:bg-teal-600',
        secondary: 'border border-teal-600 bg-white text-teal-600',
        danger:
          'border border-red-500 bg-red-500 text-white hover:bg-red-600 focus:ring-2 focus:ring-red-500 disabled:hover:bg-red-500',
        outline:
          'border border-gray-300 bg-white text-gray-800 hover:text-gray-700 disabled:hover:bg-white',
        ghost: 'bg-transparent text-gray-800 hover:bg-gray-100 disabled:hover:bg-transparent',
        bare: 'border-none bg-transparent focus:ring-0 disabled:hover:bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  }
);

export interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

function Button({
  className,
  variant,
  asChild = false,
  loading = false,
  disabled,
  children,
  ref,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-loading={loading}
      className={cn(
        buttonVariants({ variant, className }),
        loading && 'flex items-center justify-center gap-2'
      )}
      ref={ref}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <ConduitHealthLoader size="small" />}
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
